import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL

const OrgUserAuthService = {
  /**
   * Login OrgUser with username or email
   */
  login: async (username, email, password) => {
    try {
      const usernameOrEmail = String(email || username || '').trim();
      const response = await axios.post(`${API_BASE_URL}/users/loginUser`, {
        usernameOrEmail,
        password,
      });
      
      if (response.data.token) {
        sessionStorage.setItem('token', response.data.token);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        sessionStorage.setItem('role', response.data.user.role);
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Login failed' };
    }
  },

  /**
   * Logout OrgUser
   */
  logout: async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (token) {
        await axios.post(
          `${API_BASE_URL}/orguser/auth/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('role');
      sessionStorage.removeItem('organizationId');
    } catch (error) {
      // Clear local storage even if API call fails
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('role');
      sessionStorage.removeItem('organizationId');
    }
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/auth/forgot-password`,
        { email }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Password reset request failed' };
    }
  },

  /**
   * Reset password with token
   */
  resetPassword: async (resetToken, newPassword) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orguser/auth/reset-password`,
        {
          resetToken,
          newPassword,
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Password reset failed' };
    }
  },

  /**
   * Change password for logged-in user
   */
  changePassword: async (oldPassword, newPassword) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orguser/auth/change-password`,
        {
          oldPassword,
          newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Password change failed' };
    }
  },

  /**
   * Refresh access token
   */
  refreshToken: async () => {
    try {
      const refreshToken = sessionStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(
        `${API_BASE_URL}/orguser/auth/refresh-token`,
        { refreshToken }
      );

      if (response.data.token) {
        sessionStorage.setItem('token', response.data.token);
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Token refresh failed' };
    }
  },

  /**
   * Get current user from session
   */
  getCurrentUser: () => {
    try {
      const user = sessionStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: () => {
    return !!sessionStorage.getItem('token');
  },

  /**
   * Get auth header for API calls
   */
  getAuthHeader: () => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
};

export default OrgUserAuthService;
